import { Api } from '@axiom/ui';
import type {
  Candidate,
  CandidateEngagement,
  CandidateEngagementCount,
  CandidateEngagementDetails,
  Opportunity,
} from '@axiom/validation';

import { EnvUtil } from '../utils/env-util';
import type { EngagementsHistoryMarkViewedRequest } from '../schema/engagements-history-mark-viewed-request-schema';
import type { EngagementsHistorySearchRequest } from '../schema/engagements-history-search-request-schema';
import type { EngagementsHistorySearchResponse } from '../schema/engagements-history-search-response-schema';

class CandidateEngagementsApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiPortalUrl,
    });
  }

  readEngagementsCount(candidateId: Candidate['id']) {
    return super.read<{ data: CandidateEngagementCount }>({
      endpoint: `/candidates/${candidateId}/engagements/count`,
      method: 'GET',
    });
  }

  readEngagementsCurrent(candidateId: Candidate['id']) {
    return super.read<{ data: CandidateEngagement[] }>({
      endpoint: `/candidates/${candidateId}/engagements/current`,
      method: 'GET',
    });
  }

  readEngagementsCurrentDetails(
    candidateId: Candidate['id'],
    opportunityId: Opportunity['id']
  ) {
    return super.read<{ data: CandidateEngagementDetails }>({
      endpoint: `/candidates/${candidateId}/engagements/current/${opportunityId}`,
      method: 'GET',
    });
  }

  readEngagementsHistory(
    candidateId: Candidate['id'],
    body: EngagementsHistorySearchRequest
  ) {
    return super.read<EngagementsHistorySearchResponse>({
      endpoint: `/candidates/${candidateId}/engagements/history`,
      method: 'POST',
      body,
    });
  }

  async createEngagementsViewed(
    candidateId: Candidate['id'],
    body: EngagementsHistoryMarkViewedRequest
  ) {
    return super.write<EngagementsHistorySearchResponse>({
      endpoint: `/candidates/${candidateId}/engagements/history/view`,
      method: 'POST',
      body,
    });
  }

  async refreshEngagementsCount(candidateId: Candidate['id']) {
    return super.invalidate(`/candidates/${candidateId}/engagements/count`);
  }

  async refreshEngagementsCurrent(candidateId: Candidate['id']) {
    return super.invalidate(`/candidates/${candidateId}/engagements/current`);
  }

  async refreshEngagementsCurrentDetails(
    candidateId: Candidate['id'],
    opportunityId: Opportunity['id']
  ) {
    return super.invalidate(
      `/candidates/${candidateId}/engagements/current/${opportunityId}`
    );
  }

  async refreshEngagementsHistory(candidateId: Candidate['id']) {
    return super.invalidate(`/candidates/${candidateId}/engagements/history`);
  }
}

export const CandidateEngagementsApi = new CandidateEngagementsApiClass();
